/**
 * SubstitutionModal — Impact Player + Concussion Sub.
 *
 * Lets the operator swap a player on the field for one of the team's
 * named reserves (squad minus current XI). On confirm, POSTs to
 * /api/matches/<id>/substitutions/ which atomically:
 *   - replaces the player in PlayingXI (preserves batting_position / flags)
 *   - updates aggregate.current_striker / non_striker / bowler if needed
 *   - persists an audit row in MatchSubstitution
 *
 * The same modal handles both Impact (IPL) and Concussion (Law 24.1.2) —
 * different `type` prop, different default copy, same flow.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { X, ArrowRightLeft } from "lucide-react";
import { getMatchSquad, createSubstitution } from "../../api/ballbyball.api";
import useDialogDismiss from "../../hooks/useDialogDismiss";

export default function SubstitutionModal({
  open,
  onClose,
  matchId,
  type,            // "IMPACT" | "CONCUSSION"
  onApplied,       // (substitution) => void — parent should refresh session
}) {
  const [squad, setSquad] = useState(null);   // {team1, team2, …}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [teamSide, setTeamSide] = useState("team1");
  const [playerOutId, setPlayerOutId] = useState("");
  const [playerInId, setPlayerInId] = useState("");
  const [reason, setReason] = useState("");
  // Two-step confirmation — the operator hits the green button which sets
  // this to true, then a confirm overlay names the player and asks yes/no.
  // The actual POST only runs when they hit "Yes, apply".
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open || !matchId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    getMatchSquad(matchId)
      .then((res) => {
        if (!alive) return;
        setSquad(res.data);
        // Default to whichever team has reserves available
        if ((res.data?.team1?.reserves || []).length > 0)      setTeamSide("team1");
        else if ((res.data?.team2?.reserves || []).length > 0) setTeamSide("team2");
        setPlayerOutId("");
        setPlayerInId("");
        setReason("");
        setConfirming(false);
      })
      .catch((e) => alive && setError(e?.response?.data?.detail || "Failed to load squad"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, matchId]);

  const team = squad?.[teamSide];
  const xi = team?.xi || [];
  const reserves = team?.reserves || [];

  const xiOptions = useMemo(() => xi.map((p) => ({
    value: p.id,
    label: `${p.batting_position}. ${displayName(p)}${p.is_captain ? " (c)" : ""}${p.is_wicket_keeper ? " †" : ""}`,
  })), [xi]);
  const reserveOptions = useMemo(() => reserves.map((p) => ({
    value: p.id, label: displayName(p),
  })), [reserves]);

  const panelRef = useRef(null);
  useDialogDismiss({ open, onClose, ref: panelRef });

  if (!open) return null;

  const isImpact = type === "IMPACT";
  const title = isImpact ? "Impact Player Substitution" : "Concussion Substitute";
  const blurb = isImpact
    ? "IPL Impact Player rule (Law: tournament-specific). Pick one of the 5 named reserves to replace a player in the XI for the rest of the match."
    : "Law 24.1.2 — Concussion substitute. Like-for-like replacement after a head impact. Match referee approval is required.";

  // First click — validate inputs and switch to the confirm overlay
  // (the actual POST only runs after the operator hits "Yes, apply").
  const requestConfirm = () => {
    if (!playerOutId || !playerInId) {
      setError("Pick both the outgoing and incoming player.");
      return;
    }
    setError(null);
    setConfirming(true);
  };

  const handleSubmit = async () => {
    if (!playerOutId || !playerInId) {
      setError("Pick both the outgoing and incoming player.");
      setConfirming(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        type,
        team: team.team_id,
        player_out: playerOutId,
        player_in: playerInId,
        reason: reason || "",
      };
      const res = await createSubstitution(matchId, payload);
      // 2026-06-10 — AWAIT onApplied so the BBP page's session refresh
      // + optimistic players-list update finishes BEFORE the modal
      // closes. Previously we fire-and-forgot, so if the loadBallSession
      // failed silently the operator saw the old player in the picker.
      // We also pass through the in/out player ROW objects (not just
      // IDs) so BBP can optimistically swap players.batters / .bowlers
      // before the server response lands.
      if (onApplied) {
        await onApplied({
          ...res.data,
          _outPlayer: outgoingPlayer || null,
          _inPlayer: incomingPlayer || null,
          _teamSide: teamSide,
        });
      }
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.detail
        || (e?.response?.data ? JSON.stringify(e.response.data) : null)
        || e?.message
        || "Substitution failed",
      );
      // Keep the operator on the confirm step so they can retry or back out.
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  // Resolve display names for the confirm overlay. xi / reserves come from
  // the loaded squad block; the labels mirror what's already in the dropdown.
  const incomingPlayer = (squad?.[teamSide]?.reserves || []).find((p) => p.id === playerInId);
  const outgoingPlayer = (squad?.[teamSide]?.xi || []).find((p) => p.id === playerOutId);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="bg-bg-primary rounded-2xl max-w-lg w-full border border-bg-secondary shadow-2xl flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border-default">
          <div className="flex items-center gap-2 min-w-0">
            <ArrowRightLeft className={`w-5 h-5 ${isImpact ? "text-blue-400" : "text-amber-400"}`} />
            <h3 className="text-base font-semibold text-text-primary">{title}</h3>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <p className="text-xs text-text-secondary leading-snug">{blurb}</p>

          {loading && <div className="text-text-secondary text-sm">Loading squad…</div>}

          {!loading && squad && (
            <>
              {/* Format gate hint — informational, doesn't block */}
              {isImpact && !squad.impact_player_allowed && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-xs text-amber-300">
                  This match type doesn't have <span className="font-semibold">Impact Player</span> enabled in its rules. The substitution will be recorded as an audit event regardless.
                </div>
              )}

              {/* Team picker */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">Team</label>
                <div className="grid grid-cols-2 gap-2">
                  {["team1", "team2"].map((side) => (
                    <button
                      key={side}
                      onClick={() => { setTeamSide(side); setPlayerOutId(""); setPlayerInId(""); }}
                      className={`text-left px-3 py-2 rounded border text-sm ${
                        teamSide === side
                          ? "bg-button-primary/15 border-button-primary/50 text-text-primary"
                          : "bg-bg-secondary border-border-default text-text-secondary hover:text-text-primary"
                      }`}
                    >
                      {squad[side]?.team_name || side}
                      <span className="block text-[10px] text-text-secondary mt-0.5">
                        {squad[side]?.xi?.length || 0} in XI · {squad[side]?.reserves?.length || 0} reserves
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Outgoing */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                  Player going out (from XI)
                </label>
                <select
                  value={playerOutId}
                  onChange={(e) => setPlayerOutId(e.target.value)}
                  className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
                >
                  <option value="">— select —</option>
                  {xiOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {/* Incoming */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                  Player coming in (from reserves)
                </label>
                <select
                  value={playerInId}
                  onChange={(e) => setPlayerInId(e.target.value)}
                  className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
                  disabled={reserveOptions.length === 0}
                >
                  <option value="">
                    {reserveOptions.length === 0
                      ? "— no reserves listed in this team's squad —"
                      : "— select —"}
                  </option>
                  {reserveOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {reserveOptions.length === 0 && (
                  <p className="text-[11px] text-text-secondary/70 mt-1">
                    Add reserve players to this team's competition / series squad in the admin area to enable substitutions.
                  </p>
                )}
              </div>

              {/* Reason (recommended for concussion) */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                  Reason {isImpact ? "(optional)" : "— required for medical record"}
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={isImpact
                    ? "e.g. tactical move, bowling change-up"
                    : "e.g. struck on helmet, concussion symptoms"}
                  className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
                />
              </div>

              {error && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded p-2 text-xs text-rose-300 whitespace-pre-wrap break-words">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border-default">
          <button onClick={onClose} className="text-xs uppercase tracking-wider px-3 py-1.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary">
            Cancel
          </button>
          <button
            onClick={requestConfirm}
            disabled={saving || loading || !playerOutId || !playerInId}
            className={`text-xs uppercase tracking-wider px-4 py-1.5 rounded text-white disabled:opacity-40 flex items-center gap-1.5 ${
              isImpact ? "bg-blue-600 hover:bg-blue-700" : "bg-amber-600 hover:bg-amber-700"
            }`}
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
            {isImpact ? "Apply impact swap" : "Apply concussion sub"}
          </button>
        </div>

        {/* Yes/No confirmation overlay — sits on top of the modal so the
            operator sees their current selection while confirming. Picking
            "No, change selection" drops back to the picker so they can
            choose a different incoming player without losing context. */}
        {confirming && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center rounded-2xl p-4">
            <div className="bg-bg-primary rounded-xl max-w-sm w-full border border-border-default shadow-2xl p-5">
              <h4 className="text-base font-semibold text-text-primary mb-2 flex items-center gap-2">
                <ArrowRightLeft className={`w-4 h-4 ${isImpact ? "text-blue-400" : "text-amber-400"}`} />
                Confirm {isImpact ? "Impact Player" : "Concussion Substitute"}
              </h4>
              <p className="text-sm text-text-primary leading-snug mb-3">
                You have selected{" "}
                <span className="font-semibold text-emerald-300">
                  {displayName(incomingPlayer) || playerInId}
                </span>
                {" "}as {isImpact ? "Impact sub" : "Concussion sub"}
                {outgoingPlayer && (
                  <>
                    {" "}for{" "}
                    <span className="font-semibold text-rose-300">
                      {displayName(outgoingPlayer)}
                    </span>
                  </>
                )}.{" "}
                Are you sure you want to continue?
              </p>
              {error && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded p-2 text-xs text-rose-300 mb-3 whitespace-pre-wrap break-words">
                  {error}
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  disabled={saving}
                  className="text-xs uppercase tracking-wider px-3 py-1.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary disabled:opacity-40"
                >
                  No, change selection
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className={`text-xs uppercase tracking-wider px-4 py-1.5 rounded text-white disabled:opacity-40 flex items-center gap-1.5 ${
                    isImpact ? "bg-blue-600 hover:bg-blue-700" : "bg-amber-600 hover:bg-amber-700"
                  }`}
                >
                  {saving ? "Applying…" : "Yes, apply"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function displayName(p) {
  if (!p) return "";
  return p.nick_name
    || `${p.first_name || ""} ${p.last_name || ""}`.trim()
    || p.id;
}
